/**
 * Frame-exact player controller (no React). The logical playhead is integer ms on the media clock; every frame
 * operation seeks to the middle of the target frame (`frameSeekSeconds`) and then verifies what the decoder
 * actually presented via requestVideoFrameCallback's `mediaTime` (the frame's PTS). Without rVFC it falls back to
 * the `seeked` event and reports `verifiedBy: "seek"` so nobody mistakes it for a decoder check.
 *
 * Stepping always starts from the logical frame, not from `video.currentTime`, so 20 quick clicks move exactly
 * 20 frames even while seeks are still in flight (later seeks supersede earlier verifications).
 */
import { frameOf, frameSeekSeconds, frameStartMs } from "../../lib/frames";

export type FrameMeta = { mediaTime: number };

export interface VideoLike {
  currentTime: number;
  playbackRate: number;
  loop: boolean;
  muted?: boolean;
  readonly duration: number;
  readonly paused: boolean;
  readonly readyState: number;
  readonly error?: { code: number; message: string } | null;
  play(): Promise<void> | void;
  pause(): void;
  requestVideoFrameCallback?(cb: (now: number, meta: FrameMeta) => void): number;
  cancelVideoFrameCallback?(handle: number): void;
  addEventListener(type: string, fn: () => void): void;
  removeEventListener(type: string, fn: () => void): void;
}

export type LoopState = { inMs: number | null; outMs: number | null; on: boolean };

export type PlayerState = {
  fps: number;
  /** Logical playhead (ms on the media clock). */
  tMs: number;
  /** frameOf(tMs). */
  frame: number;
  /** Frame the decoder reported after the last seek / during playback; null until known. */
  shownFrame: number | null;
  verified: boolean;
  verifiedBy: "decoder" | "seek" | null;
  seeking: boolean;
  playing: boolean;
  rate: number;
  durationMs: number | null;
  ready: boolean;
  error: string | null;
  loop: LoopState;
  /** Audio muted (the element's `muted`). */
  muted: boolean;
  /** Muted because the browser refused to autoplay with sound (`play({ mutedFallback })`); a click brings it back. */
  soundBlocked: boolean;
};

export type ControllerOptions = {
  fps: number;
  durationMs?: number | null;
  frameCount?: number | null;
  verifyTimeoutMs?: number;
  maxRetries?: number;
};

export const RATES = [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2] as const;

/** The autoplay-policy refusal (play() before any user gesture, with sound). */
function isNotAllowed(e: unknown): boolean {
  return typeof e === "object" && e !== null && (e as { name?: unknown }).name === "NotAllowedError";
}

const MEDIA_ERRORS: Record<number, string> = {
  1: "aborted", 2: "network error", 3: "decode error", 4: "format not supported by this browser",
};

export class FrameController {
  private s: PlayerState;
  private readonly listeners = new Set<() => void>();
  private video: VideoLike | null = null;
  private detachFns: (() => void)[] = [];
  private seq = 0;
  private pendingFrame: number | null = null;
  private lastPresented: number | null = null;
  private playHandle: number | null = null;
  private rafHandle: number | null = null;
  private timers = new Set<ReturnType<typeof setTimeout>>();
  private frameCount: number | null;
  private readonly verifyTimeoutMs: number;
  private readonly maxRetries: number;

  constructor(opts: ControllerOptions) {
    const fps = opts.fps > 0 ? opts.fps : 30;
    this.frameCount = opts.frameCount ?? null;
    this.verifyTimeoutMs = opts.verifyTimeoutMs ?? 400;
    this.maxRetries = opts.maxRetries ?? 2;
    this.s = {
      fps, tMs: 0, frame: 0, shownFrame: null, verified: false, verifiedBy: null, seeking: false, playing: false,
      rate: 1, durationMs: opts.durationMs ?? null, ready: false, error: null,
      loop: { inMs: null, outMs: null, on: false }, muted: false, soundBlocked: false,
    };
  }

  // ------------------------------------------------------------------ store

  getState = (): PlayerState => this.s;

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  };

  private set(patch: Partial<PlayerState>): void {
    this.s = { ...this.s, ...patch };
    for (const fn of this.listeners) fn();
  }

  // ------------------------------------------------------------------ media binding

  attach(v: VideoLike | null): void {
    if (v === this.video) return;
    for (const fn of this.detachFns) fn();
    this.detachFns = [];
    this.stopPlayLoop();
    this.video = v;
    if (!v) return;
    const on = (type: string, fn: () => void) => {
      v.addEventListener(type, fn);
      this.detachFns.push(() => v.removeEventListener(type, fn));
    };
    on("loadedmetadata", () => this.onReady());
    on("error", () => this.onError());
    on("play", () => {
      if (!this.s.playing) this.set({ playing: true });
      this.startPlayLoop();
    });
    on("pause", () => {
      if (this.s.playing) this.onPaused();
    });
    on("ended", () => {
      if (this.s.playing) this.onPaused();
    });
    v.playbackRate = this.s.rate;
    v.loop = this.nativeLoop();
    v.muted = this.s.muted;
    if (v.error) this.onError();
    else if (v.readyState >= 1) this.onReady();
  }

  /** Cancels timers and loops (safe to call twice; the controller stays usable). */
  destroy(): void {
    this.stopPlayLoop();
    for (const t of this.timers) clearTimeout(t);
    this.timers.clear();
    this.seq++;
  }

  setDuration(durationMs: number | null | undefined, frameCount?: number | null): void {
    if (frameCount !== undefined) this.frameCount = frameCount;
    if (durationMs != null && durationMs !== this.s.durationMs) this.set({ durationMs });
  }

  private onReady(): void {
    const v = this.video;
    if (!v) return;
    const patch: Partial<PlayerState> = { ready: true, error: null };
    if (this.s.durationMs == null && Number.isFinite(v.duration) && v.duration > 0) {
      patch.durationMs = Math.round(v.duration * 1000);
    }
    this.set(patch);
    const k = this.pendingFrame ?? this.s.frame;
    this.pendingFrame = null;
    this.show(k);
  }

  /** Every <source> of the element failed (the error event fires on the last <source>, not on the video). */
  sourcesFailed(tried: string): void {
    this.set({ error: `no source this browser can play (tried ${tried})`, ready: false, playing: false, seeking: false });
  }

  private onError(): void {
    const e = this.video?.error;
    const msg = e ? `MediaError ${e.code} (${MEDIA_ERRORS[e.code] ?? "unknown"})${e.message ? `: ${e.message}` : ""}`
      : "media error";
    this.set({ error: msg, ready: false, playing: false, seeking: false });
  }

  // ------------------------------------------------------------------ frames

  lastFrame(): number {
    if (this.frameCount != null && this.frameCount > 0) return this.frameCount - 1;
    const d = this.s.durationMs;
    if (d != null && d > 0) return Math.max(0, Math.ceil((d * this.s.fps) / 1000 - 1e-6) - 1);
    return Number.MAX_SAFE_INTEGER;
  }

  private clampFrame(k: number): number {
    return Math.min(Math.max(0, Math.trunc(k)), this.lastFrame());
  }

  private frameAtPts(seconds: number): number {
    return Math.max(0, Math.round(seconds * this.s.fps));
  }

  /** Go to an exact ms (kept as the logical time, so "next beat" from a beat moves on). */
  seekMs(t: number): void {
    const fps = this.s.fps;
    let tt = Math.max(0, Math.round(t));
    const lf = this.lastFrame();
    if (frameOf(tt, fps) > lf) tt = frameStartMs(lf, fps);
    const frame = frameOf(tt, fps);
    if (this.s.playing && this.video) {
      this.video.currentTime = tt / 1000;
      this.set({ tMs: tt, frame });
      return;
    }
    this.set({ tMs: tt, frame, verified: false, verifiedBy: null, seeking: true });
    this.show(frame);
  }

  seekFrame(k: number): void {
    this.seekMs(frameStartMs(this.clampFrame(k), this.s.fps));
  }

  stepFrames(n: number): void {
    if (this.s.playing) this.pause();
    this.seekFrame(this.s.frame + n);
  }

  stepMs(deltaMs: number): void {
    if (this.s.playing) this.pause();
    this.seekMs(this.s.tMs + deltaMs);
  }

  private show(k: number): void {
    const seq = ++this.seq;
    if (!this.video || !this.s.ready) {
      this.pendingFrame = k;
      this.set({ seeking: false });
      return;
    }
    this.verify(seq, k, 0);
  }

  private later(fn: () => void, ms: number): ReturnType<typeof setTimeout> {
    const t = setTimeout(() => {
      this.timers.delete(t);
      fn();
    }, ms);
    this.timers.add(t);
    return t;
  }

  private verify(seq: number, k: number, attempt: number): void {
    const v = this.video;
    if (!v) return;
    const fps = this.s.fps;
    let settled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let cleanup = () => undefined as void;
    const finish = (shown: number | null, by: "decoder" | "seek") => {
      if (settled) return;
      settled = true;
      if (timer) {
        clearTimeout(timer);
        this.timers.delete(timer);
      }
      cleanup();
      if (seq !== this.seq) return; // superseded by a later seek
      if (shown !== null && shown !== k && attempt < this.maxRetries && !this.s.error) {
        this.verify(seq, k, attempt + 1);
        return;
      }
      this.set({ shownFrame: shown, verified: shown === k, verifiedBy: shown === null ? null : by, seeking: false });
    };
    const rvfc = v.requestVideoFrameCallback?.bind(v);
    if (rvfc) {
      const h = rvfc((_now, meta) => {
        const f = this.frameAtPts(meta.mediaTime);
        this.lastPresented = f;
        finish(f, "decoder");
      });
      cleanup = () => v.cancelVideoFrameCallback?.(h);
      // No new frame presented (seek inside the frame already shown): the decoder still shows lastPresented.
      timer = this.later(() => finish(this.lastPresented ?? null, "decoder"), this.verifyTimeoutMs);
    } else {
      const onSeeked = () => finish(frameOf(v.currentTime * 1000, fps), "seek");
      v.addEventListener("seeked", onSeeked);
      cleanup = () => v.removeEventListener("seeked", onSeeked);
      timer = this.later(() => finish(frameOf(v.currentTime * 1000, fps), "seek"), this.verifyTimeoutMs);
    }
    v.currentTime = frameSeekSeconds(k, fps) + this.nudge(k, attempt);
  }

  /** Retry offset: move a quarter frame towards the target if the decoder showed a neighbour. */
  private nudge(k: number, attempt: number): number {
    if (attempt === 0 || this.lastPresented === null) return 0;
    const dir = Math.sign(k - this.lastPresented);
    return (dir * 0.25 * attempt) / this.s.fps;
  }

  // ------------------------------------------------------------------ playback

  /** Start playback. `mutedFallback` (autoplay): when the browser refuses to play with sound before the user has
   * interacted with the page (NotAllowedError), play muted instead and flag `soundBlocked` (a click unmutes). */
  play(opts: { mutedFallback?: boolean } = {}): void {
    const v = this.video;
    if (!v || this.s.error) return;
    const lp = this.s.loop;
    if (lp.on && lp.inMs != null && lp.outMs != null && (this.s.tMs < lp.inMs || this.s.tMs >= lp.outMs)) {
      v.currentTime = frameSeekSeconds(frameOf(lp.inMs, this.s.fps), this.s.fps);
    }
    v.playbackRate = this.s.rate;
    this.attempt(v, !!opts.mutedFallback && !this.s.muted);
  }

  private attempt(v: VideoLike, fallback: boolean): void {
    this.set({ playing: true, seeking: false });
    this.startPlayLoop();
    const refused = (e: unknown) => {
      if (this.video !== v) return;
      this.stopPlayLoop();
      if (fallback && !this.s.muted && isNotAllowed(e)) {
        v.muted = true;
        this.set({ muted: true, soundBlocked: true });
        this.attempt(v, false);
        return;
      }
      this.set({ playing: false, error: this.s.error ?? `play() refused: ${(e as Error)?.message ?? String(e)}` });
    };
    try {
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(refused);
    } catch (e) {
      refused(e);
    }
  }

  /** Mute or unmute (the Sound toggle, the first click on the page); clears `soundBlocked`. */
  setMuted(muted: boolean): void {
    if (this.video) this.video.muted = muted;
    if (this.s.muted !== muted || this.s.soundBlocked) this.set({ muted, soundBlocked: false });
  }

  pause(): void {
    const v = this.video;
    if (v && !v.paused) v.pause();
    if (this.s.playing) this.onPaused();
  }

  togglePlay(): void {
    if (this.s.playing) this.pause();
    else this.play();
  }

  private onPaused(): void {
    this.stopPlayLoop();
    const v = this.video;
    const fps = this.s.fps;
    const f = this.lastPresented ?? (v ? frameOf(v.currentTime * 1000, fps) : this.s.frame);
    this.set({ playing: false, tMs: frameStartMs(f, fps), frame: f, shownFrame: f,
      verified: this.lastPresented !== null, verifiedBy: this.lastPresented !== null ? "decoder" : "seek" });
  }

  private startPlayLoop(): void {
    const v = this.video;
    if (!v || this.playHandle !== null || this.rafHandle !== null) return;
    const fps = this.s.fps;
    const onFrame = (f: number) => {
      const lp = this.s.loop;
      if (lp.on && lp.inMs != null && lp.outMs != null && lp.outMs > lp.inMs && frameStartMs(f, fps) >= lp.outMs) {
        v.currentTime = frameSeekSeconds(frameOf(lp.inMs, fps), fps);
      }
      if (f !== this.s.frame || !this.s.verified) {
        this.set({ tMs: frameStartMs(f, fps), frame: f, shownFrame: f, verified: true, verifiedBy: "decoder" });
      }
    };
    const rvfc = v.requestVideoFrameCallback?.bind(v);
    if (rvfc) {
      const tick = (_now: number, meta: FrameMeta) => {
        this.playHandle = null;
        if (!this.s.playing) return;
        const f = this.frameAtPts(meta.mediaTime);
        this.lastPresented = f;
        onFrame(f);
        this.playHandle = rvfc(tick);
      };
      this.playHandle = rvfc(tick);
    } else if (typeof requestAnimationFrame !== "undefined") {
      const tick = () => {
        this.rafHandle = null;
        if (!this.s.playing) return;
        onFrame(frameOf(v.currentTime * 1000, fps));
        this.rafHandle = requestAnimationFrame(tick);
      };
      this.rafHandle = requestAnimationFrame(tick);
    }
  }

  private stopPlayLoop(): void {
    if (this.playHandle !== null) this.video?.cancelVideoFrameCallback?.(this.playHandle);
    if (this.rafHandle !== null && typeof cancelAnimationFrame !== "undefined") cancelAnimationFrame(this.rafHandle);
    this.playHandle = null;
    this.rafHandle = null;
  }

  // ------------------------------------------------------------------ rate + loop

  setRate(rate: number): void {
    const r = Math.min(2, Math.max(0.25, rate));
    if (this.video) this.video.playbackRate = r;
    this.set({ rate: r });
  }

  /** Loop In–Out (both set) or, with neither set, loop the whole media natively. */
  setLoop(loop: LoopState): void {
    const cur = this.s.loop;
    if (cur.on === loop.on && cur.inMs === loop.inMs && cur.outMs === loop.outMs) return;
    this.set({ loop });
    if (this.video) this.video.loop = this.nativeLoop();
  }

  private nativeLoop(): boolean {
    const lp = this.s.loop;
    return lp.on && (lp.inMs == null || lp.outMs == null);
  }
}
